import { useUserInformationContext } from "contexts/userContext";
import { usePreferences } from "contexts/preferences-context";
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { LlmProxyService } from "services/inference/LlmProxyService";
import {
  AiOptionsWithPromptsError,
  getAiOptions,
  getAiOptionsWithPrompts,
} from "graphql/questions-and-answers/queries";
import type { AiOptions, GQLPrompt } from "graphql/questions-and-answers/queries";
import { dump } from 'js-yaml';
import { useRouteMatch } from "react-router-dom";

export type AssistantPromptSelection =
	| { readonly kind: "default" }
	| { readonly kind: "custom"; readonly prompt: GQLPrompt };

export type PromptLoadState =
	| { readonly status: "loading" }
	| { readonly status: "ready" }
	| { readonly status: "error"; readonly error: Error };

export type ServiceReadiness =
	| { readonly status: "initializing" }
	| { readonly status: "ready" }
	| { readonly status: "error"; readonly error: Error };

type LLMProxyServiceContextType = {
	readonly llmServiceRef: React.MutableRefObject<LlmProxyService | null>,
	readonly agentSlideOpen: boolean,
	readonly toggleAgentSlideOpen: () => void,
	readonly openAssistantDrawer: () => void,
	readonly pendingAssistantMessage: string | null,
	readonly queueAssistantMessage: (text: string) => void,
	readonly consumeAssistantMessage: () => void,
	readonly isServiceReady: boolean,
	readonly serviceReadiness: ServiceReadiness,
	readonly prompts: readonly GQLPrompt[],
	readonly promptSelection: AssistantPromptSelection,
	readonly selectPrompt: (prompt: GQLPrompt | null) => void,
	readonly promptLoadState: PromptLoadState,
	readonly refreshPrompts: () => Promise<void>,
	readonly clearSignal: number,
	readonly triggerClearConversation: () => void,
	readonly modelAgent: string | null,
	usageUpdateSignal: number,
	triggerUsageUpdate: () => void,
}

const DEFAULT_PROMPT_SELECTION = { kind: "default" } as const;
const INITIAL_PROMPT_LOAD_STATE = { status: "loading" } as const;
const INITIAL_SERVICE_READINESS = { status: "initializing" } as const;

const LLMProxyServiceContext = createContext<LLMProxyServiceContextType | null>(null);

export const LLMPRoxyServiceProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [agentSlideOpen, setAgentSlideOpen] = useState<boolean>(false);
  const [pendingAssistantMessage, setPendingAssistantMessage] = useState<string | null>(null);
  const [serviceReadiness, setServiceReadiness] = useState<ServiceReadiness>(INITIAL_SERVICE_READINESS);
  const [prompts, setPrompts] = useState<readonly GQLPrompt[]>([]);
  const [promptSelection, setPromptSelection] = useState<AssistantPromptSelection>(DEFAULT_PROMPT_SELECTION);
  const [promptLoadState, setPromptLoadState] = useState<PromptLoadState>(INITIAL_PROMPT_LOAD_STATE);
  const [clearSignal, setClearSignal] = useState<number>(0);
  const [usageUpdateSignal, setUsageUpdateSignal] = useState<number>(0);
  const [modelAgent, setModelAgent] = useState<string | null>(null);
  const { currentUser } = useUserInformationContext();
  const { i18n } = useTranslation();
  const { language } = usePreferences();
  const aiOptionsRef = useRef<AiOptions | null>(null);
  const { url } = useRouteMatch();
  const isServiceReady = serviceReadiness.status === "ready";

  const userYamlData = (() => {
    if (currentUser == null) {
      return "";
    }

    return dump({
      _id: currentUser._id ?? null,
      username: currentUser.username ?? null,
      first_name: currentUser.first_name ?? null,
      middle_name: currentUser.middle_name ?? null,
      last_name: currentUser.last_name ?? null,
      is_agent: currentUser.is_agent ?? false,
      is_admin: currentUser.is_admin ?? false,
      allowedRoutes: currentUser.allowedRoutes ? Array.from(currentUser.allowedRoutes) : [],
      settings: currentUser.settings ?? null,
    });
  })();

  const llmServiceRef = useRef<LlmProxyService | null>(null);

  useEffect(() => {
    const initialUserYamlData = userYamlData;

    const initService = async () => {
      let aiOptions: AiOptions;
      let loadedPrompts: GQLPrompt[] = [];

      try {
        const response = await getAiOptionsWithPrompts();
        aiOptions = response.getAiOptions;
        loadedPrompts = [...response.getAiOptions.prompts].sort((left, right) => {
          const nameOrder = left.name.localeCompare(right.name);
          return nameOrder || left._id.localeCompare(right._id);
        });
        setPrompts(loadedPrompts);
        setPromptLoadState({ status: "ready" });
      } catch (error) {
        const promptError = error instanceof Error
          ? error
          : new Error("Failed to load shared prompts");

        setPrompts([]);
        setPromptLoadState({ status: "error", error: promptError });

        if (error instanceof AiOptionsWithPromptsError && error.aiOptions) {
          aiOptions = error.aiOptions;
        } else {
          try {
            const response = await getAiOptions();
            aiOptions = response.getAiOptions;
          } catch (serviceError) {
            const readinessError = serviceError instanceof Error
              ? serviceError
              : new Error("Failed to initialize the AI service");

            setServiceReadiness({ status: "error", error: readinessError });
            // eslint-disable-next-line no-console
            console.error("Failed to initialize AI service:", serviceError);
            return;
          }
        }
      }

      try {
        aiOptionsRef.current = aiOptions;

        const systemPrompt = aiOptions.prompt_system.english
          .replace("USER_INFO", initialUserYamlData);

        setModelAgent(aiOptions.model_agent);

        llmServiceRef.current = new LlmProxyService(
          systemPrompt,
          aiOptions.model_agent,
          i18n.language
        );
        llmServiceRef.current.setUserInfo(initialUserYamlData);
        llmServiceRef.current.setPrompts(loadedPrompts);
        await llmServiceRef.current.loadMcpTools();
        setServiceReadiness({ status: "ready" });
      } catch (error) {
        const readinessError = error instanceof Error
          ? error
          : new Error("Failed to initialize the AI service");

        setServiceReadiness({ status: "error", error: readinessError });
        // eslint-disable-next-line no-console
        console.error("Failed to initialize AI service:", error);
      }
    };

    initService();
    // Service construction is mount-only; the language/user effects below push later changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (llmServiceRef.current && prompts.length > 0) {
      llmServiceRef.current.setPrompts([...prompts]);
    }
  }, [prompts]);

  useEffect(() => {
    if (llmServiceRef.current) {
      llmServiceRef.current.setLanguage(language);
    }
  }, [language]);

  const toggleAgentSlideOpen = () => setAgentSlideOpen((prevOpen) => !prevOpen);

  const openAssistantDrawer = useCallback(() => setAgentSlideOpen(true), []);

  const queueAssistantMessage = useCallback((text: string) => setPendingAssistantMessage(text), []);

  const consumeAssistantMessage = useCallback(() => setPendingAssistantMessage(null), []);

  const selectPrompt = (prompt: GQLPrompt | null) => {
    setPromptSelection(prompt === null
      ? DEFAULT_PROMPT_SELECTION
      : { kind: "custom", prompt });
  };

  const refreshPrompts = useCallback(async () => {
    try {
      const response = await getAiOptionsWithPrompts();
      const sortedPrompts = [...response.getAiOptions.prompts].sort((left, right) => {
        const nameOrder = left.name.localeCompare(right.name);
        return nameOrder || left._id.localeCompare(right._id);
      });

      setPrompts(sortedPrompts);
      setPromptLoadState({ status: "ready" });
    } catch (error) {
      // Keep the previously loaded prompts when the refresh fails.
      // eslint-disable-next-line no-console
      console.error("Failed to refresh shared prompts:", error);
    }
  }, []);

  const triggerClearConversation = useCallback(async () => {
    await llmServiceRef.current?.clearHistory();
    setClearSignal((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (promptSelection.kind !== "custom") return;
    const promptStillExists = prompts.some((prompt) => prompt._id === promptSelection.prompt._id);
    if (!promptStillExists) {
      setPromptSelection(DEFAULT_PROMPT_SELECTION);
      triggerClearConversation();
    }
  }, [prompts, promptSelection, triggerClearConversation]);

  const triggerUsageUpdate = () => {
    setUsageUpdateSignal((prev) => prev + 1);
  };

  return (
    <LLMProxyServiceContext.Provider value={{
		  llmServiceRef,
		  agentSlideOpen,
		  toggleAgentSlideOpen,
		  openAssistantDrawer,
		  pendingAssistantMessage,
		  queueAssistantMessage,
		  consumeAssistantMessage,
		  isServiceReady,
		  serviceReadiness,
		  prompts,
		  promptSelection,
		  selectPrompt,
		  refreshPrompts,
		  promptLoadState,
		  clearSignal,
		  triggerClearConversation,
		  usageUpdateSignal,
		  triggerUsageUpdate,
		  modelAgent,
    }}
    >
      {children}
    </LLMProxyServiceContext.Provider>
  );
};

export const useLLMProxyServiceContext = () => {
  const methods = useContext(LLMProxyServiceContext) as LLMProxyServiceContextType;
  return methods;
};
